import { useCallback, useEffect, useRef, useState } from 'react';
// (useRef is already imported above)
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { CanvasElement, ProjectFile } from '@archer/shared';
import { convertMermaid, PROJECT_KIND, PROJECT_SCHEMA_VERSION } from '@archer/shared';
import { CanvasBoard } from '../canvas/CanvasBoard';
import { useCanvasStore } from '../canvas/store';
import { Toolbar } from '../components/Toolbar';
import { MermaidPanel } from '../components/MermaidPanel';
import { AiDialog } from '../components/AiDialog';
import { SettingsDialog } from '../components/SettingsDialog';
import { ShareDialog } from '../components/ShareDialog';
import { getStorageAdapter, useStorage } from '../storage';
import { useAuth } from '../auth/store';
import { CloudStorageAdapter } from '../storage/cloud';
import { ArrowLeft, Check, Download, FileCode, Layers, Settings, Share2, Sparkles, Undo2, Redo2 } from 'lucide-react';
import { exportProject } from '../lib/export';
import { useHistory } from '../lib/history';

type StorageKind = 'cloud' | 'browser' | 'local';

export const EditorPage = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const api = useAuth((s) => s.api);
  const cloud = api ? new CloudStorageAdapter(api) : null;
  const kindFromQuery = (params.get('kind') as StorageKind | null) ?? null;

  const elements = useCanvasStore((s) => s.elements);
  const setElements = useCanvasStore((s) => s.setElements);
  const setViewport = useCanvasStore((s) => s.setViewport);
  const viewport = useCanvasStore((s) => s.viewport);
  const showGrid = useCanvasStore((s) => s.showGrid);
  const setShowGrid = useCanvasStore((s) => s.setShowGrid);
  const snapToGrid = useCanvasStore((s) => s.snapToGrid);
  const setSnapToGrid = useCanvasStore((s) => s.setSnapToGrid);
  const pushRecent = useStorage((s) => s.pushRecent);
  const localAdapter = useStorage((s) => s.localAdapter);

  const [name, setName] = useState('Untitled');
  const [storageRef, setStorageRef] = useState<string>(id ?? '');
  const [storageKind, setStorageKind] = useState<StorageKind>(
    kindFromQuery ?? (cloud && !localAdapter ? 'cloud' : 'browser'),
  );
  const [dirty, setDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [mermaidOpen, setMermaidOpen] = useState(false);
  const [mermaidInitial, setMermaidInitial] = useState('');
  const [aiOpen, setAiOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [role, setRole] = useState<'owner' | 'editor' | 'viewer'>('owner');
  const [loaded, setLoaded] = useState(false);

  const history = useHistory(elements, (next: unknown[]) => setElements(next as CanvasElement[]));

  const containerRef = useRef<HTMLDivElement | null>(null);
  const initialStateRef = useRef<{ name: string; elements: CanvasElement[] } | null>(null);
  const [boardSize, setBoardSize] = useState({ width: 800, height: 600 });

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setBoardSize({ width: Math.floor(width), height: Math.floor(height) });
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Load project on mount. Guarded by a ref so React.StrictMode's double-mount
  // and dependency-array re-runs don't trigger a second load that would
  // overwrite user-drawn shapes.
  const loadTokenRef = useRef(0);
  useEffect(() => {
    if (!id) return;
    const token = ++loadTokenRef.current;
    setLoaded(false);
    void (async () => {
      try {
        const adapter = getStorageAdapter(storageKind, cloud ?? undefined);
        const file = await adapter.load(id);
        if (token !== loadTokenRef.current) return; // a newer load has started; abandon
        if (file.kind !== PROJECT_KIND) throw new Error('not an Archer project');
        setName(file.name);
        setElements(file.elements);
        if (file.viewport) setViewport(file.viewport);
        initialStateRef.current = { name: file.name, elements: file.elements };
        // Use the id we requested (the URL param / storage key), not
        // file.id: the JSON blob's embedded id can be stale (e.g. a
        // locally-generated id from before the project was created on the
        // server) and must never diverge from the actual storage key.
        setStorageRef(id);
        pushRecent({ kind: storageKind, storageRef: id, name: file.name, updatedAt: file.updatedAt });
        if (storageKind === 'cloud' && cloud) {
          try {
            const { role: r } = await cloud.api.getProject(id);
            if (token !== loadTokenRef.current) return;
            setRole(r);
          } catch {
            setRole('owner');
          }
        }
        setLoaded(true);
      } catch (err) {
        if (token !== loadTokenRef.current) return;
        console.error('load failed', err);
        navigate('/projects', { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, storageKind]);

  // Mark dirty whenever the canvas or name diverges from the loaded snapshot.
  useEffect(() => {
    if (!loaded || !initialStateRef.current) return;
    const sameName = name === initialStateRef.current.name;
    const sameElements = JSON.stringify(elements) === JSON.stringify(initialStateRef.current.elements);
    setDirty(!(sameName && sameElements));
  }, [name, elements, loaded]);

  // Autosave (debounced 1.5 s)
  useEffect(() => {
    if (!loaded || !storageRef) return;
    if (!dirty) return;
    const t = setTimeout(() => void save(), 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, dirty, name, loaded]);

  const save = useCallback(async () => {
    if (!storageRef) return;
    setSaveStatus('saving');
    try {
      const adapter = getStorageAdapter(storageKind, cloud ?? undefined);
      const file: ProjectFile = {
        schemaVersion: PROJECT_SCHEMA_VERSION,
        kind: PROJECT_KIND,
        id: storageRef,
        name,
        elements,
        mermaidSources: [],
        viewport,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await adapter.save(storageRef, file);
      pushRecent({ kind: storageKind, storageRef, name, updatedAt: file.updatedAt });
      setSaveStatus('saved');
      setDirty(false);
      setTimeout(() => setSaveStatus('idle'), 1500);
    } catch (err) {
      console.error('save failed', err);
      setSaveStatus('error');
    }
  }, [storageRef, name, elements, viewport, storageKind, cloud, pushRecent]);

  const onConvertMermaid = async (code: string) => {
    const result = await convertMermaid(code);
    useCanvasStore.getState().addElements(result.elements);
    setDirty(true);
    setMermaidOpen(false);
  };

  const onAiResult = (code: string) => {
    setAiOpen(false);
    setMermaidInitial(code);
    setMermaidOpen(true);
  };

  const readOnly = role === 'viewer';

  return (
    <div className="flex h-full flex-col bg-slate-950">
      <header className="flex flex-wrap items-center gap-2 border-b border-slate-800 bg-slate-900/70 px-4 py-2">
        <button className="btn-ghost" onClick={() => navigate('/projects')}>
          <ArrowLeft className="h-4 w-4" />
        </button>
        <input
          className="min-w-[200px] bg-transparent text-sm font-semibold outline-none focus:bg-slate-800/60"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          disabled={readOnly}
        />
        <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
          {storageKind}
        </span>
        {role !== 'owner' && (
          <span className="rounded bg-amber-900/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-300">
            {role}
          </span>
        )}
        <span className="flex-1" />
        <button
          className="tool-btn"
          title="Undo (Cmd/Ctrl+Z)"
          onClick={() => history.undo()}
          disabled={readOnly}
        >
          <Undo2 className="h-4 w-4" />
        </button>
        <button
          className="tool-btn"
          title="Redo (Cmd/Ctrl+Shift+Z)"
          onClick={() => history.redo()}
          disabled={readOnly}
        >
          <Redo2 className="h-4 w-4" />
        </button>
        <button
          className="btn-ghost"
          onClick={() => setShowGrid(!showGrid)}
          data-active={showGrid}
          title="Toggle grid"
        >
          <Layers className="h-4 w-4" />
          Grid
        </button>
        <label className="flex cursor-pointer items-center gap-1 text-xs text-slate-300">
          <input
            type="checkbox"
            className="h-3 w-3"
            checked={snapToGrid}
            onChange={(e) => setSnapToGrid(e.target.checked)}
            disabled={readOnly}
          />
          Snap
        </label>
        <button className="btn-ghost" onClick={() => setMermaidOpen(true)} disabled={readOnly}>
          <FileCode className="h-4 w-4" />
          Mermaid
        </button>
        <button className="btn-ghost" onClick={() => setAiOpen(true)} disabled={readOnly}>
          <Sparkles className="h-4 w-4 text-archer-300" />
          AI
        </button>
        <button className="btn-ghost" onClick={() => setSettingsOpen(true)} title="Settings">
          <Settings className="h-4 w-4" />
        </button>
        <button
          className="btn-ghost"
          onClick={() =>
            void exportProject({
              schemaVersion: PROJECT_SCHEMA_VERSION,
              kind: PROJECT_KIND,
              id: storageRef,
              name,
              elements,
              mermaidSources: [],
              viewport,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            })
          }
          title="Export PNG / SVG / JSON"
        >
          <Download className="h-4 w-4" />
        </button>
        {storageKind === 'cloud' && storageRef && (
          <button className="btn-ghost" onClick={() => setShareOpen(true)}>
            <Share2 className="h-4 w-4" />
            Share
          </button>
        )}
        <button className="btn-primary" onClick={() => void save()} disabled={readOnly || !dirty}>
          <Check className="h-4 w-4" />
          {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : 'Save'}
        </button>
      </header>

      <div className="relative flex-1 overflow-hidden" ref={containerRef}>
        <CanvasBoard width={boardSize.width} height={boardSize.height} />
        <div className="pointer-events-none absolute left-3 top-3">
          <div className="pointer-events-auto">
            <Toolbar />
          </div>
        </div>
        <div className="pointer-events-none absolute right-3 top-3">
          <MermaidPanel
            open={mermaidOpen}
            onClose={() => setMermaidOpen(false)}
            initial={mermaidInitial}
            onConvert={onConvertMermaid}
            onAiPrompt={() => {
              setMermaidOpen(false);
              setAiOpen(true);
            }}
          />
        </div>
        <AiDialog
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          onResult={onAiResult}
          onOpenSettings={() => {
            setAiOpen(false);
            setSettingsOpen(true);
          }}
        />
        <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        {storageRef && (
          <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} projectId={storageRef} />
        )}
      </div>
    </div>
  );
};
